/**
 * A client-side router for a site with three pages, three languages, and no framework.
 *
 * The history API plus one `popstate` listener is the whole requirement here. A
 * routing library would add a dependency that has to be kept in step with React
 * itself to deliver the same thing.
 *
 * Server-side this is a single-page app: nginx answers every unknown path with
 * index.html, which is why navigating directly to /conta works rather than 404ing.
 * `/en/conta` and `/es/conta` work the same way, for the same reason.
 *
 * ## The language is part of the path
 *
 * `readLocation` splits a leading `/en` or `/es` off the path and hands the rest to
 * the route matcher. Everything below the locale behaves exactly as it did before
 * this file learned about languages, and Portuguese stays the bare path. See
 * `i18n/locale.ts` for why that last part is a hard requirement rather than a
 * preference: the desktop apps and Mercado Pago both hold unprefixed URLs.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { splitLocale, localePath, DEFAULT_LOCALE, type Locale } from './i18n/locale';

export type Route = 'home' | 'conta' | 'planos' | 'skinNFT' | 'auth-callback' | 'oauth-callback';
export type { Locale };

interface RouterValue {
  readonly route: Route;
  /** The language the current URL is spelled in. pt-BR when the path carries no prefix. */
  readonly locale: Locale;
  readonly orderId: string | null;
  navigate(route: Route, options?: { orderId?: string; locale?: Locale }): void;
}

const RouterContext = createContext<RouterValue | null>(null);

/** The locale-free path a route lives at. */
function routePath(route: Route): string {
  switch (route) {
    case 'home': return '/';
    case 'skinNFT': return '/skins';
    default: return `/${route}`;
  }
}

/**
 * The address a route has in a language, as a string.
 *
 * Exported rather than computed at each call site because it is also what the
 * `hreflang` alternates need, and two spellings of "the address of this page" that
 * agree in the app and disagree in the head tags is how a site ends up telling a
 * crawler that three different URLs are the same page.
 */
export function hrefFor(route: Route, locale: Locale, orderId?: string): string {
  const path = localePath(locale, routePath(route));
  return orderId != null ? `${path}?order=${encodeURIComponent(orderId)}` : path;
}

/**
 * Reads the current location.
 *
 * `/conta/obrigado?order=<id>` is a distinct place even though it is the account
 * page: Mercado Pago sends the buyer back here after paying, and the page needs to
 * know which order to look at. It is read from the query rather than from the path
 * because that is where the API puts it in `back_urls`.
 *
 * `/auth/callback` is where a Google sign-in started from one of the desktop apps
 * lands — the apps put it in their OAuth `redirect_to`, so it is an address that is
 * really visited rather than a route nobody ever reaches. Matched exactly, trailing
 * slash and all: a router that also accepted `/auth/callback/anything` would be a
 * second, looser spelling of the page that hands a sign-in to an app.
 */
function readLocation(): { route: Route; locale: Locale; orderId: string | null } {
  const params = new URLSearchParams(window.location.search);
  const orderId = params.get('order');

  const { locale, rest } = splitLocale(window.location.pathname);

  if (rest === '/conta' || rest.startsWith('/conta/')) {
    return { route: 'conta', locale, orderId };
  }
  if (rest === '/planos' || rest.startsWith('/planos/')) {
    return { route: 'planos', locale, orderId };
  }
  if (rest === '/skins' || rest.startsWith('/skins/')) {
    return { route: 'skinNFT', locale, orderId };
  }
  if (rest === '/auth/callback') {
    return { route: 'auth-callback', locale, orderId };
  }
  if (rest === '/auth/oauth-callback') {
    return { route: 'oauth-callback', locale, orderId };
  }
  return { route: 'home', locale, orderId };
}

export const Router: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [location, setLocation] = useState(readLocation);

  useEffect(() => {
    const onPopState = () => setLocation(readLocation());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const navigate = useCallback(
    (route: Route, options?: { orderId?: string; locale?: Locale }) => {
      const locale = options?.locale ?? location.locale;
      const href = hrefFor(route, locale, options?.orderId);

      // pushState rather than assigning `location.href`, so the page does not reload
      // and the buyer does not lose the account state they just signed in to.
      window.history.pushState(null, '', href);
      setLocation(readLocation());

      // A new page starts at the top. Without this, arriving at /conta from a long
      // scrolled homepage leaves the reader halfway down a page they have not seen.
      window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
    },
    [location.locale],
  );

  const value = useMemo<RouterValue>(
    () => ({ route: location.route, locale: location.locale, orderId: location.orderId, navigate }),
    [location, navigate],
  );

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
};

export function useRouter(): RouterValue {
  const value = useContext(RouterContext);
  if (value === null) throw new Error('useRouter must be used inside <Router>');
  return value;
}

/**
 * An anchor that navigates without a reload.
 *
 * A real `<a href>` rather than a click handler on a `<div>`, so it is
 * keyboard-operable, opens in a new tab with the modifier key, and is announced as a
 * link. All three come free with the element and none of them come free with a
 * div.
 *
 * The `href` carries the current language unless a `locale` is passed, so the same
 * link is right for a person reading in any of the three, and ⌘-clicking it opens
 * that page in that language rather than dropping back to Portuguese.
 */
export const Link: React.FC<{
  to: Route;
  children: React.ReactNode;
  className?: string;
  orderId?: string;
  /** Render the link in another language, used by the switcher. */
  locale?: Locale;
  /**
   * `aria-current`, forwarded explicitly.
   *
   * It was written at every call site for the whole life of this component and never
   * reached the DOM, because `Link` accepted no `aria-*` prop and spread no rest onto
   * the anchor. The current page was therefore styled in every state it is styled in
   * and announced in none of them, which is exactly the half of the indicator a
   * screen-reader user never sees.
   *
   * Typed narrowly rather than taken as a `rest` bag: a `Link` that forwards
   * everything is a `Link` that one day forwards an `onClick` twice.
   */
  'aria-current'?: 'page' | 'step' | 'location' | 'date' | 'time' | 'true' | 'false';
  onNavigate?: () => void;
}> = ({ to, children, className, orderId, locale, 'aria-current': ariaCurrent, onNavigate }) => {
  const { navigate, locale: currentLocale } = useRouter();
  const href = hrefFor(to, locale ?? currentLocale, orderId);

  return (
    <a
      href={href}
      className={className}
      aria-current={ariaCurrent}
      onClick={(event) => {
        // Let the browser handle a modified click: new tab, new window, download.
        // Interpreting those as navigation is the standard way to break ⌘-click.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        event.preventDefault();
        navigate(to, { orderId, locale });
        onNavigate?.();
      }}
    >
      {children}
    </a>
  );
};

export { DEFAULT_LOCALE };